import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useWallet } from '../../contexts/WalletContext';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import usePosterGenerator from '../../services/usePosterGenerator';
import usePrintables from '../../services/usePrintables';
import useCloudLibrary from '../../hooks/useCloudLibrary';
import CreditPurchaseModal from '../wallet/CreditPurchaseModal';
import StatePicker, { useSchoolContext } from './StatePicker';
import useCatalogContext from '../../services/useCatalogContext';

import DescribePanel from './aiGenerator/DescribePanel';
import GenerationProgress from './aiGenerator/GenerationProgress';
import ResultScreen from './aiGenerator/ResultScreen';
import { bandToRange } from './aiGenerator/GradeBandPicker';
import { useBackgroundGeneration } from '../../contexts/BackgroundGenerationContext';
import { handToPrint } from '../../features/print/handoff.js';

/**
 * AI poster generator — calls pse-pipeline-v3 (WEB variant).
 *
 * Intent-first panel, identical to studioCore's
 * app/src/components/home/AIGeneratorSection.jsx (2026-07-16 single-form
 * rewrite: the old Guided/Describe chooser is gone — both collected the same
 * fields). Mode state machine:
 *   'form'        → the one generator form (DescribePanel: 5 intent cards,
 *                   standards-first teach path)
 *   'generating'  → progress UI (async poll)
 *   'done'        → ResultScreen with 4 actions
 *   'rejected'    → friendly error + credits-refunded toast
 *
 * 2026-09-28 (bug batch #1, ported from studioCore): the run itself lives in BackgroundGenerationContext,
 * not here. This section starts it and WATCHES it (`fgJobId`). "Continue in
 * background" — or simply leaving the page — detaches it instead of aborting:
 * it keeps running, saves to the Library, and a toast says when it is ready.
 *
 * Web adaptations (the ONLY intended divergences from the desktop file):
 *   - saveDesign comes from useCloudLibrary (URL-direct, no client image fetch)
 *   - navigate-to-library / navigate-to-print CustomEvents become React Router
 *     navigate() calls
 *   - Print Now hands off a URL via window.pendingBrowserDownload (consumed by
 *     WebPrintPreview) — never a client fetch, because R2 CORS-blocks it
 *
 * Audience: pse-pipeline-v3 routes by JWT masterAdmin claim — regular users
 * (studioCloud signups) land in the studiocore-app audience pool automatically.
 * usePosterGenerator sends `X-Pipeline-Executor: ionos`, so web submissions run
 * on the same IONOS studioCore daemon the v1.0.8+ desktop uses.
 */

const INITIAL_FORM = {
  intent: null,            // 'teach' | 'motivate' | 'classroom' | 'school' | 'custom' | null (spec §3 Zone 1)
  subject: null,           // teach only: canonical 'Mathematics' | 'Language Arts' | 'Science' | 'Social Studies' | a raw state spelling
  subjectCategory: null,   // intent-list pick: { id, name, breadcrumb, requiresBlankFields } | null
  gradeBand: null,         // 'K-2' | '3-5' | '6-8' | '9-12' | 'all' (required — 'all' OMITS min/max)
  prompt: '',              // single freeform prompt (required)
  styleId: null,           // DB-backed style id or null for auto-pick
  standard: null,          // teach pick: { id, code, stateCode, description, gradeMin, gradeMax, stamp } | null
};

// Sticky grade band (spec §3 Zone 3): userId-scoped key (NEVER machine-global —
// shared classroom computers), 60-day expiry.
const GRADE_STICKY_TTL_MS = 60 * 24 * 60 * 60 * 1000;
const gradeStickyKey = (userId) => `pse_grade_band_${userId || 'anon'}`;
function readStickyBand(userId) {
  try {
    const raw = localStorage.getItem(gradeStickyKey(userId));
    if (!raw) return null;
    const { band, at } = JSON.parse(raw);
    if (!band || !at || Date.now() - at > GRADE_STICKY_TTL_MS) return null;
    return band;
  } catch { return null; }
}
function writeStickyBand(userId, band) {
  try {
    if (band && band !== 'all') localStorage.setItem(gradeStickyKey(userId), JSON.stringify({ band, at: Date.now() }));
  } catch { /* storage unavailable — sticky is a nicety */ }
}

export default function AIGeneratorSection({ sectionRef, initialPrompt }) {
  const { t, i18n } = useTranslation('home');
  const wallet = useWallet();
  const { showError, showInfo } = useToast();
  const generator = usePosterGenerator();
  const { user } = useAuth();
  const { saveDesign } = useCloudLibrary();
  const navigate = useNavigate();
  // Shared catalog context (2026-08-19): one module-cached GET /api/states and
  // one jurisdiction value for the whole app, so a country picked in the
  // account modal shows up here (and in Printables) without a reload.
  const catalog = useCatalogContext();
  // Still needed for the ask-once modal gating (.loaded / .skipped / .skip).
  const schoolContext = useSchoolContext();
  const { fetchPosterAlignment } = usePrintables();

  // US states + territories + countries; empty until it loads / on offline —
  // StatePicker + TeachPanel fall back to the hardcoded US list.
  const jurisdictions = catalog.jurisdictions;

  // Single-mode since 2026-07-16: 'form' | 'generating' | 'done' | 'rejected'.
  const [mode, setMode] = useState('form');
  // Session-only dismissal of the state picker (backdrop/Esc); distinct from
  // the permanent localStorage skip.
  const [pickerDismissed, setPickerDismissed] = useState(false);
  // The state picker opens only when the teacher expands the standard picker
  // without a known state — the one moment the state is functionally needed.
  const [stateAsk, setStateAsk] = useState(false);
  const [form, setForm] = useState({ ...INITIAL_FORM, prompt: initialPrompt || '' });
  // Sticky band restore — rendered visually distinct until the teacher touches it.
  const [restoredBand, setRestoredBand] = useState(false);
  useEffect(() => {
    const band = readStickyBand(user?.userId ?? user?.id);
    if (band) {
      setForm((f) => (f.gradeBand ? f : { ...f, gradeBand: band }));
      setRestoredBand(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleFormChange = useCallback((next) => {
    setForm((prev) => {
      if (next.gradeBand !== prev.gradeBand) {
        setRestoredBand(false);
        writeStickyBand(user?.userId ?? user?.id, next.gradeBand);
      }
      return next;
    });
  }, [user]);
  const [lastResult, setLastResult] = useState(null);    // { poster, design, prompt }
  const [rejection, setRejection] = useState(null);       // { creditsRefunded, kind }
  const [showInsufficient, setShowInsufficient] = useState(false);
  const bg = useBackgroundGeneration();
  // The run this section is watching (null = none). Progress / preview / the
  // final result are read from the provider's job.
  const [fgJobId, setFgJobId] = useState(null);
  const fgJob = fgJobId ? bg.jobs[fgJobId] : null;
  const fgJobIdRef = useRef(null);
  fgJobIdRef.current = fgJobId;
  const pollState = fgJob?.pollState ?? null;
  const finalising = !!fgJob?.finalising;

  const creditCost = wallet.getCost('ai_poster_generate');

  // Leaving the page mid-generation no longer aborts it (it used to: the
  // credit was spent and the finished poster never reached the Library). The
  // run is handed to the background; the provider toasts when it is saved.
  useEffect(() => {
    return () => {
      if (fgJobIdRef.current) bg.detach(fgJobIdRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Build the prompt sent to the pipeline. The user-typed prompt is primary;
   * we append subject-category path and grade band as context so the architect
   * has structured hints even when the user's prose didn't mention them.
   */
  const composeFullPrompt = useCallback((f) => {
    const parts = [f.prompt?.trim() || ''];
    if (f.intent === 'teach' && f.subject) {
      parts.push(`Subject area: ${f.subject}.`);
    } else if (f.subjectCategory) {
      const path = Array.isArray(f.subjectCategory.breadcrumb)
        ? f.subjectCategory.breadcrumb.map((b) => b.name).join(' › ')
        : (f.subjectCategory.breadcrumb || f.subjectCategory.name);
      parts.push(`Subject area: ${path}.`);
    }
    if (f.gradeBand && f.gradeBand !== 'all') {
      parts.push(`Intended for grade ${f.gradeBand} students.`);
    }
    return parts.filter(Boolean).join(' ');
  }, []);

  const runFlow = useCallback(async () => {
    const minLen = (form.standard?.code || form.subjectCategory?.id) ? 4 : 10;
    if (!form.prompt || form.prompt.trim().length < minLen || !form.gradeBand) return;

    const composedPrompt = composeFullPrompt(form);
    // 'all' OMITS both bounds (spec §3 Zone 3 — explicit 0/12 would override
    // prose-derived grades and unfence IIG concept resolution).
    const { gradeMin, gradeMax } = form.gradeBand === 'all'
      ? { gradeMin: null, gradeMax: null }
      : bandToRange(form.gradeBand);

    setMode('generating');
    setRejection(null);
    setLastResult(null);

    const id = bg.start({
      title: form.prompt.trim().slice(0, 60),
      prompt: composedPrompt,
      // spec §4: subject is sent ONLY on the teach path (canonical string);
      // intent-list picks omit it (the expander infers).
      subject: form.intent === 'teach' ? (form.subject || undefined) : undefined,
      subjectCategoryId: form.subjectCategory?.id || undefined,
      intent: form.intent || undefined,
      gradeMin: gradeMin ?? undefined,
      gradeMax: gradeMax ?? undefined,
      styleId: form.styleId,
      // IIG Phase 4 — attach the picked standard + footer-stamp opt-in and let
      // generationFlow fetch the alignment for the result chip / library card.
      requestedStandardId: form.standard?.id,
      stampStandardCode: form.standard ? form.standard.stamp !== false : false,
      // Content language (2026-07-22): the app's i18n selection drives the
      // poster's language; omitted for English (shipped-installer contract).
      languageCode: (() => { const l = i18n.resolvedLanguage || i18n.language; return l && l !== 'en' && !l.startsWith('en-') ? l : undefined; })(),
      viewerStateCode: schoolContext.stateCode,
      fetchAlignment: fetchPosterAlignment,
      wallet: {
        debit: wallet.debit,
        canAfford: wallet.canAfford,
        getCost: wallet.getCost,
        refreshBalance: wallet.refreshBalance,
      },
      generator,
      saveDesign,
    });
    setFgJobId(id);
  }, [form, composeFullPrompt, wallet, generator, saveDesign, schoolContext.stateCode, fetchPosterAlignment, i18n, bg]);

  // Tier A5 (2026-09-08): show the poster the moment the backend is done. The
  // result screen renders in a 'saving' state (actions disabled) until the
  // library save completes and the full result replaces this.
  useEffect(() => {
    if (!fgJob?.preview || fgJob.result) return;
    const { poster, prompt: shownPrompt } = fgJob.preview;
    setLastResult((cur) => (cur && !cur.saving ? cur : { poster, design: null, prompt: shownPrompt, saving: true }));
    setMode('done');
  }, [fgJob?.preview, fgJob?.result]);

  // The watched run finished: same outcome handling as before.
  useEffect(() => {
    const result = fgJob?.result;
    if (!result) return;
    bg.release(fgJobId);
    setFgJobId(null);

    if (result.status === 'insufficient') {
      setMode('form');
      setShowInsufficient(true);
      return;
    }

    if (result.status === 'cancelled') {
      showInfo(t('generator.cancelledToast'));
      setMode('form');
      return;
    }

    if (result.status === 'rejected') {
      setRejection({
        creditsRefunded: result.creditsRefunded,
        // 'quality' | 'technical' — copy is chosen from i18n; the backend's
        // raw error text is never shown to customers (console log only).
        kind: result.rejectionKind || 'technical',
      });
      setMode('rejected');
      if (result.creditsRefunded && result.creditsRefunded > 0) {
        showInfo(t('generator.refundedToast', { count: result.creditsRefunded }));
      }
      return;
    }

    if (result.status === 'error') {
      showError(result.error || t('generator.generationFailed'));
      setMode('form');
      return;
    }

    // success
    setLastResult(result);
    setMode('done');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fgJob?.result]);

  // "Continue in background": stop watching, keep the run going.
  const handleContinueInBackground = useCallback(() => {
    if (!fgJobId) return;
    bg.detach(fgJobId);
    setFgJobId(null);
    setLastResult(null);
    setMode('form');
    showInfo(t('generator.background.started'));
  }, [bg, fgJobId, showInfo, t]);

  const handleShowInLibrary = useCallback(() => {
    setMode('form');
    navigate('/library');
  }, [navigate]);

  // Web Print Now: URL handoff to /print (no client fetch — R2 CORS-blocks
  // cross-origin fetch but <img src> works, and WebPrintPreview's mount-time
  // consumer handles the URL form).
  const handlePrintNow = useCallback(() => {
    const poster = lastResult?.poster;
    if (!poster) return;

    const url = poster.full_res_path || poster.preview_path || poster.thumbnail_path;
    if (!url) {
      showError(t('generator.printPrepareFailed'));
      return;
    }
    // Brightivity: the Print page copies the original server-side (same handoff as Library / Printables).
    handToPrint([{ remote: true, posterId: poster.id || null, name: poster.title || 'AI Poster', previewUrl: poster.thumbnail_path || url, sourceUrl: url }]);
    navigate('/print');
  }, [lastResult, showError, navigate, t]);

  const handleGenerateSimilar = useCallback(() => {
    // Keep form state so the user can tweak and resubmit.
    setLastResult(null);
    setRejection(null);
    setMode('form');
  }, []);

  const handleGenerateFresh = useCallback(() => {
    setForm({ ...INITIAL_FORM });
    setLastResult(null);
    setRejection(null);
    setMode('form');
  }, []);

  const handleCancel = useCallback(() => {
    if (fgJobId) bg.cancel(fgJobId);
  }, [bg, fgJobId]);

  // ─── Render ───────────────────────────────────────────────────

  return (
    <section
      ref={sectionRef}
      style={{
        minHeight: 580,
        fontFamily: 'inherit',
      }}
    >
      {/* Intent-first panel (spec §3): the teach path INCLUDES the standards
          search (the promoted StandardPicker mechanism), so no separate
          picker renders below the form anymore. */}
      {mode === 'form' && bg.backgroundCount > 0 && (
        <div style={{
          maxWidth: 560, margin: '0 auto 14px', padding: '9px 14px', borderRadius: 10,
          background: 'rgba(127,75,246,0.08)', border: '1px solid rgba(127,75,246,0.25)',
          color: 'rgba(127,75,246,0.95)', fontSize: '0.8rem', textAlign: 'center',
        }}>
          {t('generator.background.running', { count: bg.backgroundCount })}
        </div>
      )}

      {mode === 'form' && (
        <DescribePanel
          value={form}
          onChange={handleFormChange}
          onSubmit={runFlow}
          creditCost={creditCost}
          stateCode={schoolContext.stateCode}
          onStateChange={(c) => schoolContext.saveState(c)}
          onNeedState={() => setStateAsk(true)}
          jurisdictions={jurisdictions}
          token={user?.token}
          restoredBand={restoredBand}
        />
      )}

      {mode === 'generating' && (
        <GenerationProgress
          pollState={pollState}
          finalising={finalising}
          onCancel={handleCancel}
          onBackground={handleContinueInBackground}
          startedAt={fgJob?.startedAt}
          standardCode={form.standard?.code ?? null}
        />
      )}

      {mode === 'done' && lastResult && (
        <ResultScreen
          poster={lastResult.poster}
          design={lastResult.design}
          saving={!!lastResult.saving}
          prompt={lastResult.prompt}
          alignment={lastResult?.design?.d_metaData?.alignment ?? null}
          viewerStateCode={schoolContext.stateCode}
          onShowInLibrary={handleShowInLibrary}
          onPrintNow={handlePrintNow}
          onGenerateSimilar={handleGenerateSimilar}
          onGenerateFresh={handleGenerateFresh}
        />
      )}

      {mode === 'rejected' && rejection && (
        <div style={{
          maxWidth: 480, margin: '0 auto', padding: '36px 24px', textAlign: 'center',
          color: 'rgba(27,37,80,0.807)',
          background: '#FFFFFF', borderRadius: 16,
          border: '1px solid rgba(27,37,80,0.076)',
        }}>
          <div style={{ fontSize: '2.2rem', marginBottom: 12 }}>🎨</div>
          <h2 style={{ margin: '0 0 10px', fontSize: '1.1rem', fontWeight: 700 }}>
            {t('generator.rejected.title')}
          </h2>
          <p style={{ margin: '0 0 8px', fontSize: '0.86rem', color: 'rgba(27,37,80,0.57)', lineHeight: 1.5 }}>
            {rejection.kind === 'quality'
              ? t('generator.rejected.fallbackMessage')
              : t('generator.rejected.technicalMessage')}
          </p>
          {/* Refund confirmation stands alone — the one thing a teacher needs
              to KNOW (not infer) after a failed spend. */}
          {rejection.creditsRefunded > 0 && (
            <div style={{
              display: 'inline-block', margin: '4px 0 18px',
              padding: '7px 14px', borderRadius: 999,
              background: 'rgba(38,199,116,0.12)', border: '1px solid rgba(38,199,116,0.3)',
              color: 'rgba(38,199,116,0.95)', fontSize: '0.8rem', fontWeight: 600,
            }}>
              <Trans
                t={t}
                i18nKey="generator.rejected.refunded"
                count={rejection.creditsRefunded}
                components={{ strong: <strong /> }}
              />
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: rejection.creditsRefunded > 0 ? 0 : 12 }}>
            <button
              onClick={handleGenerateSimilar}
              style={{
                padding: '13px 22px', borderRadius: 12, border: 'none',
                background: 'linear-gradient(135deg, #7F4BF6, #9B6DFF)', color: '#FFFFFF',
                boxShadow: '0 6px 22px rgba(127,75,246,0.3)',
                fontSize: '0.88rem', fontWeight: 800, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {t('generator.rejected.tryAgain')}
            </button>
            <button
              onClick={handleGenerateFresh}
              style={{
                padding: '13px 20px', borderRadius: 12,
                border: '1px solid rgba(27,37,80,0.114)',
                background: 'transparent', color: 'rgba(27,37,80,0.617)',
                fontSize: '0.86rem', fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {t('result.generateFresh')}
            </button>
          </div>
        </div>
      )}

      <CreditPurchaseModal
        isOpen={showInsufficient}
        onClose={() => setShowInsufficient(false)}
      />

      {/* IIG Phase 4 — ask-once state picker. The form is the landing view, so
          "entered a flow" no longer signals intent: the picker opens ONLY when
          the teacher expands the standard picker without a known state.
          Backdrop/Esc dismisses for the session; "Skip for now" is permanent. */}
      <StatePicker
        open={stateAsk && schoolContext.loaded && !catalog.jurisdiction && !schoolContext.skipped && !pickerDismissed}
        onSave={(c) => catalog.setJurisdiction(c)}
        onSkip={schoolContext.skip}
        onDismiss={() => setPickerDismissed(true)}
        jurisdictions={jurisdictions}
      />
    </section>
  );
}
